import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  ROLE_LIST_DROPDOWN,
} from "../../routes/routes";
import { toast } from "react-toastify";
import { ValidationMessage, hasRole } from "../../components/Utils";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { usePermission } from "../../hooks/usePermission";
import {
  ADMIN_TYPES,
  GEO_LEVEL_FILTER_ROLES,
  GEO_LEVEL_META,
  canCreateAdminType,
  getCreatableAdminTypes,
} from "./adminTypeConfig";

const CreateAdmin = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [searchParams] = useSearchParams();
  const auth_user = JSON.parse(localStorage.getItem("user"));

  const creatableTypes = useMemo(
    () => getCreatableAdminTypes(hasPermission, hasRole),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const typeOptions = useMemo(
    () => [
      { value: "", label: "Select" },
      ...creatableTypes.map((t) => ({ value: t.key, label: t.label })),
    ],
    [creatableTypes]
  );

  const initialType = (() => {
    const q = searchParams.get("type");
    if (q && canCreateAdminType(q, hasPermission, hasRole)) return q;
    return null;
  })();

  const [adminTypeKey, setAdminTypeKey] = useState(initialType);
  const config = adminTypeKey ? ADMIN_TYPES[adminTypeKey] : null;

  const [form, setForm] = useState({ mobile_country_code: "91" });
  const [filter, setFilter] = useState({});
  const [errors, setErrors] = useState({});
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [type, setType] = useState("password");
  const [type1, setType1] = useState("password");

  const selectCountryRef = useRef(null);
  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const clearSelect = (ref) => {
    if (ref.current?.clearValue) ref.current.clearValue();
  };

  const handleCountryClear = () => {
    clearSelect(selectCountryRef);
    clearSelect(selectStateRef);
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleStateClear = () => {
    clearSelect(selectStateRef);
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleCityClear = () => {
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleRegionClear = () => {
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleChapterClear = () => {
    clearSelect(selectChapterRef);
  };

  const showGeoLevel = (level) =>
    !!config?.geoLevels?.includes(level) &&
    hasRole(GEO_LEVEL_FILTER_ROLES[level]);

  const applyScopedDefaults = () => {
    let scopedForm = {};
    let nextFilter = {};
    const cid = auth_user?.country_id ? Number(auth_user.country_id) : undefined;
    const sid = auth_user?.state_id ? Number(auth_user.state_id) : undefined;
    const cityId = auth_user?.city_id ? Number(auth_user.city_id) : undefined;
    const rid = auth_user?.region_id ? Number(auth_user.region_id) : undefined;

    if (hasRole([6])) {
      if (cid) {
        scopedForm.country_id = cid;
        nextFilter.country_id = cid;
      }
    }
    if (hasRole([7])) {
      if (cid) scopedForm.country_id = cid;
      if (sid) scopedForm.state_id = sid;
      nextFilter = { ...nextFilter, country_id: cid, state_id: sid };
    }
    if (hasRole([8])) {
      if (cid) scopedForm.country_id = cid;
      if (sid) scopedForm.state_id = sid;
      if (cityId) scopedForm.city_id = cityId;
      nextFilter = { ...nextFilter, country_id: cid, state_id: sid, city_id: cityId };
    }
    if (hasRole([2])) {
      if (cid) scopedForm.country_id = cid;
      if (sid) scopedForm.state_id = sid;
      if (cityId) scopedForm.city_id = cityId;
      if (rid) scopedForm.region_id = rid;
      nextFilter = { ...nextFilter, country_id: cid, state_id: sid, city_id: cityId, region_id: rid };
    }

    setForm((prev) => ({
      mobile_country_code: prev?.mobile_country_code || "91",
      country_id: prev?.country_id || scopedForm.country_id,
      country_id_selected: prev?.country_id_selected,
      ...scopedForm,
    }));
    setFilter((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const changeAdminType = (selected) => {
    const key = selected?.value ? selected.value : null;
    setAdminTypeKey(key);
    setErrors((prev) => ({ ...prev, admin_type: undefined, admin_role_id: undefined }));
    handleStateClear();
    setForm((prev) => {
      const preservedCountryId = prev?.country_id;
      const preservedCountrySelected = prev?.country_id_selected;
      let next = {
        mobile_country_code: prev?.mobile_country_code || "91",
        admin_role_id: null,
      };
      if (preservedCountryId) {
        next.country_id = preservedCountryId;
        next.country_id_selected = preservedCountrySelected;
      }
      return next;
    });
    applyScopedDefaults();
    if (!key) {
      setRoleOptions([]);
    }
  };

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!adminTypeKey || !config) {
      const newErrors = {
        admin_type: ["Admin Type is required."],
      };
      if (!form?.name) newErrors.name = ["Name is required."];
      if (!form?.email) newErrors.email = ["Email Address is required."];
      if (!form?.mobile_number) newErrors.mobile_number = ["Mobile number is required."];
      if (!form?.password) newErrors.password = ["Password is required."];
      if (!form?.confirm_password) newErrors.confirm_password = ["Confirm Password is required."];
      if (!form?.admin_role_id) newErrors.admin_role_id = ["Roles and Permission is required."];
      setErrors(newErrors);
      toast.error("Please fill all required fields");
      return;
    }
    setBtnDisabled(true);
    let requestBody = { ...form, admin_type: config.key };
    if (requestBody.country_id) {
      requestBody.country_id = Number(requestBody.country_id);
    }
    axios
      .post(config.api.create, requestBody)
      .then((res) => {
        if (res.status === 201 && res.data.success) {
          toast.success(config.toasts.created);
          navigate("/admins");
        }
      })
      .catch((err) => {
        const data = err.response?.data;
        if (data?.error && typeof data.error === "object" && !Array.isArray(data.error)) {
          setErrors(data.error);
        } else if (data?.error) {
          setErrors(data.error);
        }
        toast.error(data?.msg || "Something went wrong");
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  useEffect(() => {
    applyScopedDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        const fetchedCountryOptions = countryResponse.data.data || [];
        setCountryOptions(fetchedCountryOptions);

        if (fetchedCountryOptions.length === 1) {
          const onlyCountry = fetchedCountryOptions[0];
          setForm((prev) => {
            if (!prev?.country_id) {
              return {
                ...prev,
                country_id: Number(onlyCountry.value),
                country_id_selected: onlyCountry,
              };
            }
            return prev;
          });
          setFilter((prev) => {
            if (!prev?.country_id) {
              return {
                ...prev,
                country_id: Number(onlyCountry.value),
              };
            }
            return prev;
          });
        }

        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setStateOptions(stateResponse.data.data || []);

        const cityResponse = await axios.get(CITY_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setCityOptions(cityResponse.data.data || []);

        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setRegionOptions(regionResponse.data.data || []);

        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setChapterOptions(chapterResponse.data.data || []);

        if (adminTypeKey) {
          const roleResponse = await axios.get(ROLE_LIST_DROPDOWN, {
            params: { admin_type: adminTypeKey },
          });
          setRoleOptions(roleResponse.data.data || []);
        } else {
          setRoleOptions([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [filter, adminTypeKey]);

  const optionsByLevel = {
    country: countryOptions,
    state: stateOptions,
    city: cityOptions,
    region: regionOptions,
    chapter: chapterOptions,
  };
  const refByLevel = {
    country: selectCountryRef,
    state: selectStateRef,
    city: selectCityRef,
    region: selectRegionRef,
    chapter: selectChapterRef,
  };

  const accessParentKey = config
    ? GEO_LEVEL_META[config.geoLevels[config.geoLevels.length - 1]].filterKey
    : null;
  const accessOptions =
    config?.access && form?.[accessParentKey]
      ? optionsByLevel[config.access.level]
      : [];

  const onGeoChange = (level, selected) => {
    if (level === "country") {
      handleStateClear();
      setForm({
        ...form,
        country_id: selected?.value ? Number(selected.value) : null,
        country_id_selected: selected || null,
        state_id: null,
        state_id_selected: null,
        city_id: null,
        city_id_selected: null,
        region_id: null,
        region_id_selected: null,
        chapter_id: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        country_id: selected?.value ? Number(selected.value) : null,
        state_id: null,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "state") {
      handleCityClear();
      setForm({
        ...form,
        state_id: selected?.value ? Number(selected.value) : null,
        state_id_selected: selected || null,
        city_id: null,
        city_id_selected: null,
        region_id: null,
        region_id_selected: null,
        chapter_id: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        state_id: selected?.value ? Number(selected.value) : null,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "city") {
      handleRegionClear();
      setForm({
        ...form,
        city_id: selected?.value ? Number(selected.value) : null,
        city_id_selected: selected || null,
        region_id: null,
        region_id_selected: null,
        chapter_id: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        city_id: selected?.value ? Number(selected.value) : null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "region") {
      handleChapterClear();
      setForm({
        ...form,
        region_id: selected?.value ? Number(selected.value) : null,
        region_id_selected: selected || null,
        chapter_id: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        region_id: selected?.value ? Number(selected.value) : null,
        chapter_id: null,
      });
      return;
    }
    if (level === "chapter") {
      setForm({
        ...form,
        chapter_id: selected?.value ? Number(selected.value) : null,
        chapter_id_selected: selected || null,
      });
      setFilter({
        ...filter,
        chapter_id: selected?.value ? Number(selected.value) : null,
      });
    }
  };

  if (creatableTypes.length === 0) {
    return (
      <div className="content-wrapper">
        <div className="card icard p-4">
          <p className="mb-0">No admin types available to create.</p>
          <Link to="/admins" className="mt-3">
            Back to Admins
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Admins</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <Link to={"/admins"}>
                    <span>Admins List</span>
                  </Link>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Create Admin</span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <Link to={"/admins"}>
                  <button type="button" className="btn btn-dark igoback_btn">
                    <i className="bx bx-exit"></i> back
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="row">
          <div className="col-xl-12">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center" />
              <div className="card-body">
                <form autoComplete="off" onSubmit={submit}>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <label htmlFor="admin_type" className="form-label">
                        Admin Type<span className="text-danger">*</span>
                      </label>
                      <Select
                        name="admin_type"
                        isClearable
                        options={typeOptions}
                        placeholder="Select"
                        value={
                          adminTypeKey
                            ? typeOptions.find((o) => o.value === adminTypeKey)
                            : typeOptions.find((o) => o.value === "")
                        }
                        onChange={changeAdminType}
                      />
                      <ValidationMessage error={errors} name="admin_type" />
                    </div>

                    {config &&
                      config.geoLevels.map((level) => {
                        if (!showGeoLevel(level)) return null;
                        const meta = GEO_LEVEL_META[level];
                        return (
                          <div className="col-6 mb-3" key={level}>
                            <label
                              htmlFor={meta.filterKey}
                              className="form-label"
                            >
                              {meta.label}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              isClearable
                              ref={refByLevel[level] || undefined}
                              name={meta.filterKey}
                              options={optionsByLevel[level]}
                              value={
                                form?.[meta.selectedKey] ||
                                optionsByLevel[level]?.find(
                                  (opt) =>
                                    Number(opt.value) ===
                                    Number(form?.[meta.filterKey])
                                ) ||
                                null
                              }
                              onChange={(selected) =>
                                onGeoChange(level, selected)
                              }
                            />
                            <ValidationMessage
                              error={errors}
                              name={meta.filterKey}
                            />
                          </div>
                        );
                      })}

                    {config?.access && (
                      <div className="col-6 mb-3">
                        <label htmlFor="access_ids" className="form-label">
                          {config.access.label}
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti={true}
                          name="access_ids"
                          options={accessOptions}
                          value={accessOptions.filter((opt) =>
                            (form?.access_ids || []).includes(Number(opt.value))
                          )}
                          onChange={(selected) =>
                            setForm({
                              ...form,
                              access_ids: selected
                                ? selected.map((opt) => Number(opt.value))
                                : [],
                            })
                          }
                        />
                        <ValidationMessage error={errors} name="access_ids" />
                      </div>
                    )}

                    <div className="col-6 mb-3">
                      <label htmlFor="admin_role_id" className="form-label">
                      Roles and Permission<span className="text-danger">*</span>
                      </label>
                      <Select
                        isClearable
                        isDisabled={!adminTypeKey}
                        placeholder={
                          !adminTypeKey
                            ? "Select Admin Type first"
                            : roleOptions.length === 0
                            ? "No roles found for this Admin Type"
                            : "Select"
                        }
                        options={roleOptions}
                        value={
                          form?.admin_role_id
                            ? roleOptions.find(
                                (r) => Number(r.value) === Number(form.admin_role_id)
                              ) || null
                            : null
                        }
                        onChange={(selected) =>
                          setForm({
                            ...form,
                            admin_role_id: selected ? Number(selected.value) : null,
                          })
                        }
                      />
                      <ValidationMessage
                        error={errors}
                        name="admin_role_id"
                      />
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="name">
                          Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="admin_name"
                          placeholder="Name"
                          autoComplete="off"
                          value={form?.name || ""}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="name" />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="email">
                          Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="admin_email"
                          placeholder="Email"
                          autoComplete="off"
                          value={form?.email || ""}
                          onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="email" />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label
                          className="form-label"
                          htmlFor="mobile_number"
                        >
                          Mobile Number
                          <span className="text-danger">*</span>
                        </label>
                        <PhoneInput
                          country="in"
                          preferredCountries={["in"]}
                          enableSearch={true}
                          searchPlaceholder="Search country or code..."
                          searchNotFound="No country found"
                          searchStyle={{
                            width: "88%",
                            padding: "6px 8px",
                            fontSize: "14px",
                          }}
                          countryCodeEditable={true}
                          value={`${form?.mobile_country_code || "91"}${
                            form?.mobile_number || ""
                          }`}
                          onChange={(value, country) => {
                            const dial = country?.dialCode
                              ? String(country.dialCode)
                              : form?.mobile_country_code || "91";
                            const rawNumber =
                              country?.dialCode && value.startsWith(dial)
                                ? value.slice(dial.length)
                                : value;
                            setForm({
                              ...form,
                              mobile_country_code: dial,
                              mobile_number: rawNumber,
                            });
                          }}
                          inputProps={{
                            id: "mobile_number",
                            name: "mobile_number",
                          }}
                          containerClass="w-100"
                          inputClass="form-control"
                          inputStyle={{ width: "100%" }}
                        />
                        <ValidationMessage
                          error={errors}
                          name="mobile_number"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="password">
                          Password<span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-merge">
                          <input
                            type={type}
                            className="form-control"
                            id="password"
                            name="admin_password"
                            placeholder="***********"
                            autoComplete="new-password"
                            value={form?.password || ""}
                            onChange={(e) =>
                              setForm({ ...form, password: e.target.value })
                            }
                          />
                          {type === "password" ? (
                            <span
                              className="input-group-text cursor-pointer"
                              id="basic-default-password"
                              onClick={() => setType("text")}
                            >
                              <i className="bx bx-hide"></i>
                            </span>
                          ) : (
                            <span
                              className="input-group-text cursor-pointer"
                              id="basic-default-password"
                              onClick={() => setType("password")}
                            >
                              <i className="bx bx-show"></i>
                            </span>
                          )}
                        </div>
                        <ValidationMessage error={errors} name="password" />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label
                          className="form-label"
                          htmlFor="confirm_password"
                        >
                          Confirm Password
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-merge">
                          <input
                            type={type1}
                            className="form-control"
                            id="confirm_password"
                            name="admin_confirm_password"
                            placeholder="***********"
                            autoComplete="new-password"
                            value={form?.confirm_password || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                confirm_password: e.target.value,
                              })
                            }
                          />
                          {type1 === "password" ? (
                            <span
                              className="input-group-text cursor-pointer"
                              id="basic-default-password"
                              onClick={() => setType1("text")}
                            >
                              <i className="bx bx-hide"></i>
                            </span>
                          ) : (
                            <span
                              className="input-group-text cursor-pointer"
                              id="basic-default-password"
                              onClick={() => setType1("password")}
                            >
                              <i className="bx bx-show"></i>
                            </span>
                          )}
                        </div>
                        <ValidationMessage
                          error={errors}
                          name="confirm_password"
                        />
                      </div>
                    </div>

                    <div className="demo-inline-spacing d-flex justify-content-end">
                      <Link to={"/admins"}>
                        <button
                          type="button"
                          className="btn btn-primary iclose_btn"
                        >
                          Close
                        </button>
                      </Link>
                      <button
                        disabled={btnDisabled}
                        type="submit"
                        className="btn btn-success isubmit_button"
                        onClick={submit}
                      >
                        &nbsp; Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAdmin;
